import { useCallback, useEffect } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { CoreFormFieldText } from '@bambi/component-library/src/components/forms/fields/CoreFormFieldText';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from 'react-hook-form';

import { useAppDispatch } from 'src/app/store/hooks';
import {
  MtmTransportationProvider,
  useBrokersMtmTransportationProvidersCreateMutation,
  useBrokersMtmTransportationProvidersRetrieveQuery,
  useBrokersMtmTransportationProvidersUpdateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { show } from 'src/common/primitives/Toast/toast.slice';

import { adminActions } from '../admin.slice';

interface MTMSettingsFormProps {
  onSubmit: SubmitHandler<MtmTransportationProvider>;
  onCancel?: () => void;
}

export function MTMSettingsForm({ onSubmit, onCancel }: MTMSettingsFormProps) {
  const form = useFormContext<MtmTransportationProvider>();

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4">
      <Controller
        control={form.control}
        name="org_code"
        rules={{ required: 'This field is required' }}
        render={({ field, fieldState }) => (
          <CoreFormFieldText
            label="Vendor Code (MTM's 'code' for an organization) *"
            inputProps={field}
            error={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={form.control}
        name="provider"
        render={({ field }) => (
          <CoreFormFieldText
            label="Provider (MTM's name for an organization)"
            inputProps={field}
          />
        )}
      />
      <Controller
        control={form.control}
        name="client_id"
        render={({ field }) => (
          <CoreFormFieldText
            label="OAuth Client ID (MTM OAuth client ID for an organization)"
            inputProps={field}
          />
        )}
      />
      <div className="flex justify-between">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" onClick={form.handleSubmit(onSubmit)}>
          Save
        </Button>
      </div>
    </div>
  );
}

interface MTMSettingsFormControllerProps {
  settingsId?: string | null;
}
export function MTMSettingsFormController({
  settingsId,
}: MTMSettingsFormControllerProps) {
  const { data, isLoading } = useBrokersMtmTransportationProvidersRetrieveQuery(
    {
      id: settingsId!,
    },
    {
      skip: !settingsId,
    }
  );

  const dispatch = useAppDispatch();

  const form = useForm<MtmTransportationProvider>({
    defaultValues: data,
  });

  const [triggerCreateMtmTransportationProvider] =
    useBrokersMtmTransportationProvidersCreateMutation();

  const [triggerUpdateMtmTransportationProvider] =
    useBrokersMtmTransportationProvidersUpdateMutation();

  const handleSubmit = useCallback<SubmitHandler<MtmTransportationProvider>>(
    async (data) => {
      try {
        if (!settingsId) {
          await triggerCreateMtmTransportationProvider({
            mtmTransportationProvider: data,
          }).unwrap();
        } else {
          await triggerUpdateMtmTransportationProvider({
            id: settingsId,
            mtmTransportationProvider: data,
          }).unwrap();
        }
        dispatch(
          show({
            title: 'MTM transportation provider updated successfully',
            type: 'success',
          })
        );
        dispatch(adminActions.setEditingMtmSettingsId(null));
        dispatch(adminActions.setShowMtmSettingsModal(false));
        form.reset();
      } catch {
        dispatch(
          show({
            title: 'Failed to update MTM transportation provider',
            type: 'error',
          })
        );
      }
    },
    [
      triggerCreateMtmTransportationProvider,
      triggerUpdateMtmTransportationProvider,
      dispatch,
      settingsId,
      form,
    ]
  );

  const handleCancel = useCallback(() => {
    dispatch(adminActions.setEditingMtmSettingsId(null));
    dispatch(adminActions.setShowMtmSettingsModal(false));
  }, [dispatch]);

  useEffect(() => {
    form.reset(data);
  }, [data, form]);

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <MTMSettingsForm onSubmit={handleSubmit} onCancel={handleCancel} />
    </FormProvider>
  );
}
